import React, { useEffect, useMemo, useState, useRef } from "react";
import { jsPDF } from "jspdf";
import {
  X,
  XCircle,
  User,
  Scale,
  Package,
  Wallet,
  CheckCircle2,
  Truck,
  Store,
  Zap,
  MapPin,
  Plus,
  Download,
  MessageCircle,
} from "lucide-react";
import Swal from "sweetalert2";
import {
  getCustomers,
  createCustomer,
} from "../../api/customers/customers.api";
import { getServices } from "../../api/services/services.api";
import { createOrder } from "../../api/orders/orders.api";
import Receipt58mm from "../receipt/Receipt58mm";

const OrderModal = ({ isOpen, onClose, onSuccess }) => {
  const [paymentStatus, setPaymentStatus] = useState("Belum Lunas");
  const [paymentMethod, setPaymentMethod] = useState("CASH");
  const [totalHarga, setTotalHarga] = useState(0);
  const [receiptData, setReceiptData] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const printRef = useRef();
  const handlePrint = () => {
    if (!printRef.current) return;

    const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(
      navigator.userAgent
    );

    if (isMobile) {
      setShowReceipt(true);

      setTimeout(() => {
        window.print();
      }, 300);

      return;
    }

    const printContents = printRef.current.innerHTML;

    const printWindow = window.open("", "_blank", "width=420,height=700");

    if (!printWindow) {
      Swal.fire(
        "Popup diblokir",
        "Izinkan pop-up browser agar struk bisa dicetak",
        "warning"
      );
      return;
    }

    printWindow.document.open();
    printWindow.document.write(`
    <!doctype html>
    <html>
      <head>
        <title>Print Receipt</title>
        <style>
          @page {
            size: 58mm auto;
            margin: 0;
          }

          html, body {
            width: 58mm;
            margin: 0;
            padding: 0;
            background: #fff;
            font-family: monospace;
          }

          * {
            box-sizing: border-box;
          }
        </style>
      </head>
      <body>
        ${printContents}
      </body>
    </html>
  `);

    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
      }, 250);
    };
  };

  const handlePrintBluetooth = () => {
    if (!receiptData) return;

    const formatRupiah = (n) =>
      new Intl.NumberFormat("id-ID").format(Number(n || 0));

    const now = new Date().toLocaleString("id-ID");
    const hargaPerKg = Number(receiptData.service?.harga || 0);
    const berat = Number(receiptData.berat || 0);
    const customerName = receiptData.customer?.nama || "-";
    const customerPhone = receiptData.customer?.no_hp || "-";
    const serviceName = receiptData.service?.nama || "-";
    const payment = receiptData.metode || "Belum Dibayar";
    const estimatedDate = receiptData.estimasi_selesai
      ? new Date(receiptData.estimasi_selesai).toLocaleDateString("id-ID")
      : "";

    // Format ini mengikuti dua bagian pada Receipt58mm: pelanggan dan copy internal.
    let text = "";
    text += "================================\n";
    text += "         QUEEN LAUNDRY          \n";
    text += "  Laundry Express & Premium Care\n";
    text += "       Jl. Contoh No. 123      \n";
    text += "       Telp. 0812-xxxx-xxxx    \n";
    text += "================================\n";
    text += `Invoice : ${receiptData.kode_invoice || "-"}\n`;
    text += `Tanggal : ${now}\n`;
    text += "--------------------------------\n";
    text += "PELANGGAN\n";
    text += `${customerName}\n`;
    text += `${customerPhone}\n`;
    text += "--------------------------------\n";
    text += "DETAIL ORDER\n";
    text += `Layanan  : ${serviceName}\n`;
    text += `Berat    : ${berat} Kg\n`;
    text += `Harga/Kg : Rp ${formatRupiah(hargaPerKg)}\n`;
    text += "--------------------------------\n";
    text += `TOTAL    : Rp ${formatRupiah(receiptData.total)}\n`;
    text += `Pembayaran: ${payment}\n`;
    if (estimatedDate) text += `Est. Selesai: ${estimatedDate}\n`;
    text += "================================\n";
    text += "  Terima kasih telah menggunakan\n";
    text += "          QUEEN LAUNDRY         \n";
    text += "================================\n";
    text += "         COPY INTERNAL           \n";
    text += "================================\n";
    text += `Invoice : ${receiptData.kode_invoice || "-"}\n`;
    text += `Tanggal : ${now}\n`;
    text += "--------------------------------\n";
    text += "IDENTITAS\n";
    text += `Nama    : ${customerName}\n`;
    text += `No HP   : ${customerPhone}\n`;
    text += "--------------------------------\n";
    text += "DETAIL CUCIAN\n";
    text += `Layanan : ${serviceName}\n`;
    text += `Berat   : ${berat} Kg\n`;
    text += `Harga/Kg: Rp ${formatRupiah(hargaPerKg)}\n`;
    text += `Total   : Rp ${formatRupiah(receiptData.total)}\n`;
    text += `Pembayaran: ${payment}\n`;
    if (estimatedDate) text += `Est. Selesai: ${estimatedDate}\n`;
    text += "--------------------------------\n";
    text += "CHECKLIST\n";
    text += "[ ] Sudah dicuci\n";
    text += "[ ] Sudah dikeringkan\n";
    text += "[ ] Sudah disetrika\n";
    text += "[ ] Sudah dibungkus\n";
    text += "--------------------------------\n";
    text += "    TEMPEL DI PLASTIK LAUNDRY   \n";
    text += "================================\n\n\n"; // Beri jarak potong kertas

    // Encode teks agar aman dikirim via URL Intent
    const encodedText = encodeURIComponent(text);

    // Mengirim perintah langsung ke aplikasi RawBT di HP Android
    window.location.href = `rawbt:text/${encodedText}`;
  };

  const handleDownloadPdf = () => {
    if (!receiptData) return;

    const formatRupiah = (n) =>
      new Intl.NumberFormat("id-ID").format(Number(n || 0));
    const pdf = new jsPDF({ unit: "mm", format: [58, 180] });
    const lines = [
      "QUEEN LAUNDRY",
      "Laundry Express & Premium Care",
      "Jl. Contoh No. 123",
      "Telp. 0812-xxxx-xxxx",
      "--------------------------------",
      `Invoice: ${receiptData.kode_invoice || "-"}`,
      `Tanggal: ${new Date().toLocaleString("id-ID")}`,
      "--------------------------------",
      `Pelanggan: ${receiptData.customer?.nama || "-"}`,
      `No. HP: ${receiptData.customer?.no_hp || "-"}`,
      `Layanan: ${receiptData.service?.nama || "-"}`,
      `Berat: ${receiptData.berat || 0} Kg`,
      `Harga/Kg: Rp ${formatRupiah(receiptData.service?.harga)}`,
      "--------------------------------",
      `TOTAL: Rp ${formatRupiah(receiptData.total)}`,
      `Pembayaran: ${receiptData.metode || "-"}`,
      "",
      "Terima kasih",
    ];

    pdf.setFont("courier", "normal");
    pdf.setFontSize(8);
    lines.forEach((line, index) => pdf.text(line, 4, 8 + index * 6));
    pdf.save(`${receiptData.kode_invoice || "order"}.pdf`);
  };

  const handleSendWhatsApp = () => {
    if (!receiptData?.customer?.no_hp) {
      Swal.fire(
        "Nomor WhatsApp tidak tersedia",
        "Customer belum memiliki nomor HP",
        "warning"
      );
      return;
    }

    const phone = String(receiptData.customer.no_hp).replace(/\D/g, "");
    const internationalPhone = phone.startsWith("0")
      ? `62${phone.slice(1)}`
      : phone;
    const formatRupiah = (n) =>
      new Intl.NumberFormat("id-ID").format(Number(n || 0));
    const message = [
      `Halo ${receiptData.customer.nama || "Customer"},`,
      `Order laundry Anda berhasil dibuat dengan invoice ${
        receiptData.kode_invoice || "-"
      }.`,
      `Layanan: ${receiptData.service?.nama || "-"}`,
      `Total: Rp ${formatRupiah(receiptData.total)}`,
      receiptData.estimasi_selesai
        ? `Estimasi selesai: ${new Date(
            receiptData.estimasi_selesai
          ).toLocaleDateString("id-ID")}`
        : "",
      "Terima kasih.",
    ]
      .filter(Boolean)
      .join("\n");

    window.open(
      `https://wa.me/${internationalPhone}?text=${encodeURIComponent(message)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const [serviceType, setServiceType] = useState("reguler");
  const [isExpress, setIsExpress] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const [serviceQuery, setServiceQuery] = useState("");

  const filteredServices = useMemo(() => {
    if (!serviceQuery.trim()) return services;

    const q = serviceQuery.toLowerCase();

    return services.filter((s) => s.nama?.toLowerCase().includes(q));
  }, [services, serviceQuery]);

  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    id_customer: "",
    id_service: "",
    berat: "",
    catatan: "",
    ongkir: "",
    estimasi_selesai: "",
  });

  const selectedService = useMemo(() => {
    return services.find((s) => s.id_service == form.id_service);
  }, [services, form.id_service]);

  useEffect(() => {
    if (!selectedService || !form.berat) {
      setTotalHarga(0);
      return;
    }

    const berat = Number(form.berat);
    const harga = Number(selectedService.harga);
    const ongkir = serviceType !== "reguler" ? Number(form.ongkir) || 0 : 0;

    setTotalHarga(berat * harga + ongkir);
  }, [selectedService, form.berat, form.ongkir, serviceType]);

  const getAutoCatatan = () => {
    if (serviceType === "pickup") return "pickup";
    if (serviceType === "pickup_delivery") return "pickup_delivery";
    return "reguler";
  };

  // customer picker
  const [customerQuery, setCustomerQuery] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showQuickAddCustomer, setShowQuickAddCustomer] = useState(false);

  const [newCustomer, setNewCustomer] = useState({
    nama: "",
    no_hp: "",
    alamat: "",
  });

  useEffect(() => {
    if (isOpen) {
      fetchInitialData();
    }
  }, [isOpen]);

  const fetchInitialData = async () => {
    try {
      const custRes = await getCustomers();
      setCustomers(Array.isArray(custRes) ? custRes : []);
    } catch (error) {
      console.error(error);
      Swal.fire("Error", error.message || "Gagal memuat customer", "error");
    }

    try {
      const serviceRes = await getServices();
      setServices(Array.isArray(serviceRes) ? serviceRes : []);
    } catch (error) {
      console.error(error);
      Swal.fire("Error", error.message || "Gagal memuat layanan", "error");
    }
  };

  const filteredCustomers = useMemo(() => {
    if (!customerQuery.trim()) return customers;

    const q = customerQuery.toLowerCase();

    return customers.filter(
      (cust) =>
        cust.nama?.toLowerCase().includes(q) ||
        cust.no_hp?.toLowerCase().includes(q)
    );
  }, [customers, customerQuery]);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    if (serviceType === "reguler") {
      setForm((prev) => ({
        ...prev,
        ongkir: "",
      }));
    }
  }, [serviceType]);

  const handleQuickAddCustomer = async () => {
    if (!newCustomer.nama || !newCustomer.no_hp) {
      Swal.fire("Oops", "Nama dan no HP wajib diisi", "warning");
      return;
    }

    try {
      const created = await createCustomer(newCustomer);

      const customer = created?.id_customer
        ? created
        : {
            ...newCustomer,
            id_customer: Date.now(),
          };

      setCustomers((prev) => [customer, ...prev]);

      handleChange("id_customer", customer.id_customer);
      setSelectedCustomer(customer);
      setCustomerQuery(customer.nama);
      setShowQuickAddCustomer(false);
      setShowCustomerDropdown(false);

      setNewCustomer({
        nama: "",
        no_hp: "",
        alamat: "",
      });

      Swal.fire({
        title: "Pelanggan ditambahkan",
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire("Error", error.message || "Gagal menambah pelanggan", "error");
    }
  };

  const resetForm = () => {
    setForm({
      id_customer: "",
      id_service: "",
      berat: "",
      catatan: "",
      ongkir: "",
      estimasi_selesai: "",
    });

    setIsExpress(false);
    setPaymentStatus("Belum Lunas");
    setPaymentMethod("CASH");
    setServiceType("reguler");

    setCustomerQuery("");
    setShowCustomerDropdown(false);
    setShowQuickAddCustomer(false);

    setNewCustomer({
      nama: "",
      no_hp: "",
      alamat: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.id_customer || !form.id_service || !form.berat) {
      Swal.fire(
        "Form belum lengkap",
        "Pilih customer, layanan, dan isi berat",
        "warning"
      );
      return;
    }

    if (paymentStatus === "Lunas" && !paymentMethod) {
      Swal.fire(
        "Metode pembayaran belum dipilih",
        "Pilih metode pembayaran untuk status Lunas",
        "warning"
      );
      return;
    }

    if (
      (serviceType === "pickup" || serviceType === "pickup_delivery") &&
      (!form.ongkir || Number(form.ongkir) <= 0 || !form.estimasi_selesai)
    ) {
      Swal.fire(
        "Data pickup belum lengkap",
        "Isi ongkir dan estimasi selesai untuk layanan pickup",
        "warning"
      );
      return;
    }

    try {
      setLoading(true);

      const payload = {
        berat: Number(form.berat),
        catatan: getAutoCatatan(),
        id_customer: Number(form.id_customer),
        id_service: Number(form.id_service),
        langsung_bayar: paymentStatus === "Lunas",
        metode: paymentMethod || "CASH",
        ongkir: serviceType !== "reguler" ? Number(form.ongkir) || 0 : 0,
        estimasi_selesai: form.estimasi_selesai || "",
      };

      const res = await createOrder(payload);

      const receipt = {
        kode_invoice: res.invoice || res.kode_invoice,
        berat: form.berat,
        customer: selectedCustomer,
        service: selectedService,
        total: totalHarga,
        metode: paymentStatus === "Lunas" ? paymentMethod : "Belum Lunas",
        estimasi_selesai: form.estimasi_selesai,
      };

      setReceiptData(receipt);
      setShowReceipt(true);

      Swal.fire({
        title: "Order berhasil dibuat",
        icon: "success",
        timer: 1000,
        showConfirmButton: false,
      });

      if (onSuccess) onSuccess();
    } catch (error) {
      Swal.fire("Error", error.message || "Gagal membuat order", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !showReceipt) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-end sm:items-center justify-center">
      <div
        className="fixed inset-0 bg-slate-900/40 backdrop-blur-md"
        onClick={onClose}
      />

      <div className="relative bg-white dark:bg-slate-800 w-full max-w-lg rounded-t-[2.5rem] sm:rounded-3xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom sm:zoom-in duration-300 z-[1000]">
        <div className="sm:hidden w-12 h-1.5 bg-gray-300 dark:bg-slate-600 rounded-full mx-auto mt-4 mb-2"></div>

        <div className="bg-queen-navy p-6 text-white flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold flex items-center gap-2">
              <CheckCircle2 className="text-queen-gold" size={24} />
              Buat Order Baru
            </h3>
            <p className="text-blue-200 text-xs mt-1">
              Lengkapi rincian cucian pelanggan
            </p>
          </div>

          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full"
          >
            <X size={24} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-6 space-y-6 max-h-[75vh] overflow-y-auto"
        >
          {/* CUSTOMER */}
          <div className="space-y-2 relative">
            <label className="label">
              <User size={14} /> Pelanggan
            </label>

            {!form.id_customer ? (
              <>
                <input
                  type="text"
                  className="input"
                  placeholder="Cari nama / nomor HP..."
                  value={customerQuery}
                  onChange={(e) => {
                    setCustomerQuery(e.target.value);
                    setShowCustomerDropdown(true);
                    setShowQuickAddCustomer(false);
                  }}
                  onFocus={() => {
                    setShowCustomerDropdown(true);
                    setShowQuickAddCustomer(false);
                  }}
                />

                {showCustomerDropdown && customerQuery.trim() && (
                  <div className="absolute z-50 mt-1 w-full rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl max-h-56 overflow-y-auto">
                    {filteredCustomers.length > 0 ? (
                      filteredCustomers.map((cust) => (
                        <button
                          key={cust.id_customer}
                          type="button"
                          onClick={() => {
                            handleChange("id_customer", cust.id_customer);
                            setCustomerQuery(cust.nama);
                            setSelectedCustomer(cust);
                            setShowCustomerDropdown(false);
                            setShowQuickAddCustomer(false);
                          }}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-slate-700 transition"
                        >
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-2xl bg-queen-gold/10 text-queen-navy dark:text-queen-gold flex items-center justify-center text-sm font-black shrink-0">
                              {cust.nama?.charAt(0)?.toUpperCase()}
                            </div>

                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-slate-800 dark:text-white truncate">
                                {cust.nama}
                              </p>

                              {cust.no_hp && (
                                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                  {cust.no_hp}
                                </p>
                              )}
                            </div>
                          </div>
                        </button>
                      ))
                    ) : (
                      <button
                        type="button"
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-slate-700 transition"
                        onClick={() => {
                          setShowQuickAddCustomer(true);
                          setShowCustomerDropdown(false);
                          setNewCustomer({
                            nama: customerQuery,
                            no_hp: "",
                            alamat: "",
                          });
                        }}
                      >
                        <div className="flex items-center gap-2 text-queen-navy dark:text-queen-gold font-semibold">
                          <Plus size={14} />
                          Tambah pelanggan baru
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          "{customerQuery}" belum ditemukan
                        </p>
                      </button>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center justify-between rounded-2xl border border-emerald-200 bg-emerald-50 dark:bg-emerald-500/10 dark:border-emerald-500/20 px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-2xl bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 flex items-center justify-center text-sm font-black shrink-0">
                    {(selectedCustomer?.nama || customerQuery)
                      ?.charAt(0)
                      ?.toUpperCase()}
                  </div>

                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300 truncate">
                      {selectedCustomer?.nama || customerQuery}
                    </p>

                    {selectedCustomer?.no_hp && (
                      <p className="text-[11px] text-emerald-600 dark:text-emerald-400 truncate">
                        {selectedCustomer.no_hp}
                      </p>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    handleChange("id_customer", "");
                    setCustomerQuery("");
                    setSelectedCustomer(null);
                    setShowCustomerDropdown(false);
                    setShowQuickAddCustomer(false);
                  }}
                  className="p-1 text-emerald-700 hover:bg-emerald-100 dark:hover:bg-emerald-500/10 rounded-full transition"
                >
                  <XCircle size={18} />
                </button>
              </div>
            )}
          </div>

          {/* QUICK ADD CUSTOMER */}
          {showQuickAddCustomer && !form.id_customer && (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-4 space-y-3 animate-in fade-in duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-queen-navy dark:text-white">
                    Tambah Pelanggan Baru
                  </p>
                  <p className="text-[11px] text-slate-500">
                    Data ini akan langsung dipilih untuk order
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setShowQuickAddCustomer(false);
                  }}
                  className="p-1 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition"
                >
                  <X size={16} />
                </button>
              </div>

              <input
                className="input"
                placeholder="Nama pelanggan"
                value={newCustomer.nama}
                onChange={(e) =>
                  setNewCustomer((prev) => ({
                    ...prev,
                    nama: e.target.value,
                  }))
                }
              />

              <input
                className="input"
                placeholder="Nomor HP"
                value={newCustomer.no_hp}
                onChange={(e) =>
                  setNewCustomer((prev) => ({
                    ...prev,
                    no_hp: e.target.value,
                  }))
                }
              />

              <textarea
                rows={2}
                className="input resize-none"
                placeholder="Alamat"
                value={newCustomer.alamat}
                onChange={(e) =>
                  setNewCustomer((prev) => ({
                    ...prev,
                    alamat: e.target.value,
                  }))
                }
              />

              <button
                type="button"
                onClick={handleQuickAddCustomer}
                className="w-full py-3 rounded-2xl bg-queen-navy text-white font-bold hover:opacity-95 transition"
              >
                Simpan Pelanggan
              </button>
            </div>
          )}

          {/* SERVICE TYPE */}
          <div className="space-y-3">
            <label className="label">
              <Package size={14} /> Tipe Layanan
            </label>

            <div className="grid grid-cols-3 gap-3">
              {[
                ["reguler", "Reguler", Store],
                ["pickup", "Pickup", Truck],
                ["pickup_delivery", "Pickup + Delivery", Truck],
              ].map(([key, label, Icon]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setServiceType(key)}
                  className={`flex flex-col items-center justify-center gap-2 py-4 rounded-2xl border transition-all ${
                    serviceType === key
                      ? "bg-queen-navy text-white border-queen-navy shadow-lg scale-[1.02]"
                      : "bg-gray-50 dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-500"
                  }`}
                >
                  <Icon size={18} />
                  <span className="text-[10px] font-bold text-center">
                    {label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* SERVICE */}
          <div className="space-y-2 relative">
            <label className="label">
              <Package size={14} /> Layanan
            </label>

            {!form.id_service ? (
              <>
                <input
                  type="text"
                  className="input"
                  placeholder="Cari layanan..."
                  value={serviceQuery}
                  onChange={(e) => {
                    setServiceQuery(e.target.value);
                    setShowServiceDropdown(true);
                  }}
                  onFocus={() => setShowServiceDropdown(true)}
                />

                {showServiceDropdown && serviceQuery.trim() && (
                  <div className="absolute z-50 mt-1 w-full rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl max-h-56 overflow-y-auto">
                    {filteredServices.length > 0 ? (
                      filteredServices.map((service) => (
                        <button
                          key={service.id_service}
                          type="button"
                          onClick={() => {
                            handleChange("id_service", service.id_service);
                            setServiceQuery(service.nama);
                            setShowServiceDropdown(false);
                          }}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-slate-700 transition"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-slate-800 dark:text-white truncate">
                                {service.nama}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                {service.tipe}
                              </p>
                            </div>

                            <p className="text-sm font-black text-queen-navy dark:text-queen-gold whitespace-nowrap">
                              Rp {Number(service.harga).toLocaleString("id-ID")}
                            </p>
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-sm text-gray-500">
                        Layanan tidak ditemukan
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center justify-between rounded-2xl border border-emerald-200 bg-emerald-50 dark:bg-emerald-500/10 dark:border-emerald-500/20 px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-2xl bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 flex items-center justify-center text-sm font-black">
                    {selectedService?.nama?.charAt(0)}
                  </div>

                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300 truncate">
                      {selectedService?.nama}
                    </p>
                    <p className="text-[11px] text-emerald-600 dark:text-emerald-400 truncate">
                      {selectedService?.tipe}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    handleChange("id_service", "");
                    setServiceQuery("");
                    setShowServiceDropdown(false);
                  }}
                  className="p-1 text-emerald-700 hover:bg-emerald-100 dark:hover:bg-emerald-500/10 rounded-full"
                >
                  <XCircle size={18} />
                </button>
              </div>
            )}
          </div>

          {/* BERAT - DIPINDAH KE BAWAH */}
          <div className="space-y-2 mt-4">
            <label className="label">
              <Scale size={14} /> Berat (Kg)
            </label>

            <input
              type="number"
              step="0.1"
              className="input"
              placeholder="Contoh: 3.5"
              value={form.berat}
              onChange={(e) => handleChange("berat", e.target.value)}
            />

            {/* helper UX kecil */}
            <p className="text-[11px] text-gray-400">
              Masukkan berat cucian dalam kilogram
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {serviceType !== "reguler" && (
              <div className="space-y-2">
                <label className="label">
                  <MapPin size={14} /> Ongkir
                </label>
                <input
                  type="number"
                  min="0"
                  className="input"
                  placeholder="Masukkan ongkir"
                  value={form.ongkir}
                  onChange={(e) => handleChange("ongkir", e.target.value)}
                />
              </div>
            )}

            <div className="space-y-2">
              <label className="label">
                <Zap size={14} /> Estimasi Selesai
              </label>
              <input
                type="date"
                className="input"
                value={form.estimasi_selesai}
                onChange={(e) =>
                  handleChange("estimasi_selesai", e.target.value)
                }
              />
            </div>
          </div>

          <div className="p-5 bg-queen-navy/5 dark:bg-slate-700/50 rounded-2xl border border-dashed border-queen-navy/20 dark:border-slate-600">
            <label className="text-[10px] font-bold text-queen-navy dark:text-queen-gold uppercase tracking-wider">
              Total Tagihan
            </label>

            <div className="flex items-center gap-2 mt-1">
              <span className="text-2xl font-bold text-queen-navy dark:text-white">
                Rp
              </span>

              <div className="text-3xl font-black text-queen-navy dark:text-white w-full">
                {totalHarga.toLocaleString("id-ID")}
              </div>
            </div>
          </div>
          {/* PAYMENT METHOD */}
          {paymentStatus === "Lunas" && (
            <div className="space-y-2">
              <label className="label">
                <Wallet size={14} /> Metode Pembayaran
              </label>

              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="input"
              >
                <option>CASH</option>
                <option>TRANSFER</option>
                <option>QRIS</option>
              </select>
            </div>
          )}

          {/* PAYMENT STATUS */}
          <div className="space-y-3">
            <label className="label">
              <Wallet size={14} /> Status Pembayaran
            </label>

            <div className="grid grid-cols-2 gap-3 p-1 bg-gray-100 dark:bg-slate-900 rounded-2xl">
              <button
                type="button"
                onClick={() => setPaymentStatus("Lunas")}
                className={`btn-toggle ${
                  paymentStatus === "Lunas" ? "bg-green-500 text-white" : ""
                }`}
              >
                Lunas
              </button>

              <button
                type="button"
                onClick={() => setPaymentStatus("Belum Lunas")}
                className={`btn-toggle ${
                  paymentStatus === "Belum Lunas" ? "bg-red-500 text-white" : ""
                }`}
              >
                Belum
              </button>
            </div>
          </div>

          <button
            disabled={loading}
            className="w-full py-5 bg-queen-gold text-queen-navy font-black rounded-2xl shadow-xl disabled:opacity-60"
          >
            {loading ? "Menyimpan..." : "Konfirmasi Order"}
          </button>
        </form>
      </div>

      <style>{`
  .label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    color: #9ca3af;
    letter-spacing: 0.1em;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .input {
    width: 100%;
    padding: 14px 16px;
    border-radius: 16px;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    outline: none;
  }

  .dark .input {
    background: #0f172a;
    border-color: #334155;
    color: white;
  }

  .btn-toggle {
    padding: 12px;
    border-radius: 12px;
    font-weight: bold;
    font-size: 14px;
    transition: 0.3s;
  }

  @media print {
    body * {
      visibility: hidden !important;
    }

    #thermal-print,
    #thermal-print * {
      visibility: visible !important;
    }

    #thermal-print {
      position: absolute;
      left: 0;
      top: 0;
      width: 58mm;
      margin: 0;
      padding: 0;
      background: white;
    }

    button {
      display: none !important;
    }
  }
`}</style>
      {showReceipt && receiptData && (
        <div className="fixed inset-0 z-[9999] bg-black/60 flex items-end sm:items-center justify-center">
          <div className="bg-white w-full sm:w-auto max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-xl shadow-xl p-3">
            <div ref={printRef} id="thermal-print">
              <Receipt58mm data={receiptData} />
            </div>

            <p className="text-center text-sm font-semibold text-slate-700">
              Pilih tindakan untuk order ini
            </p>
            <div className="flex flex-col gap-2 mt-3 sticky bottom-0 bg-white pt-2">
              <button
                onClick={handlePrintBluetooth}
                className="w-full py-3 bg-emerald-600 text-white font-bold rounded-lg hover:bg-emerald-700 transition flex items-center justify-center gap-2"
              >
                Cetak via Bluetooth (HP)
              </button>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <button
                  onClick={handlePrint}
                  className="py-3 bg-queen-navy text-white rounded-lg text-sm"
                >
                  Cetak Standard
                </button>

                <button
                  onClick={handleDownloadPdf}
                  className="py-3 bg-slate-700 text-white rounded-lg text-sm flex items-center justify-center gap-1"
                >
                  <Download size={16} /> PDF
                </button>

                <button
                  onClick={handleSendWhatsApp}
                  className="py-3 bg-green-600 text-white rounded-lg text-sm flex items-center justify-center gap-1"
                >
                  <MessageCircle size={16} /> Kirim WA
                </button>
              </div>

              <button
                onClick={() => {
                  setShowReceipt(false);
                  setReceiptData(null);
                  resetForm();
                  onClose();
                  onSuccess?.();
                }}
                className="w-full py-3 bg-black text-white rounded-lg text-sm"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderModal;
